import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Mail, Send, CheckCircle, HelpCircle, Bug, Lightbulb, Clock, Briefcase, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../lib/helpers";

const SUBJECT_OPTIONS = [
  { value: "general", label: "General Question" },
  { value: "technical", label: "Technical Issue" },
  { value: "data", label: "Report Incorrect Property Data" },
  { value: "feature", label: "Feature Request" },
  { value: "billing", label: "Billing or Subscription" },
];

const QUICK_LINKS = [
  { icon: HelpCircle, label: "FAQs", subject: null, link: "/faq" },
  { icon: Bug, label: "Report Data Issue", subject: "data" },
  { icon: Lightbulb, label: "Request Feature", subject: "feature" },
];

export default function SupportContact() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", subject: "", message: "",
    account_email: "", property_address: "",
  });
  const [showOptional, setShowOptional] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  const handleQuickLink = (link) => {
    if (link.link) {
      navigate(link.link);
      return;
    }
    if (link.subject) {
      handleChange("subject", link.subject);
      document.getElementById("support-form")?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/support`, form);
      setSubmitted(true);
      toast.success("Message sent successfully!");
    } catch {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setForm({ name: "", email: "", subject: "", message: "", account_email: "", property_address: "" });
    setSubmitted(false);
    setShowOptional(false);
  };

  return (
    <section className="py-16 sm:py-20 px-4 sm:px-6 border-t border-border bg-secondary/10" data-testid="support-contact-section" id="support-contact">
      <div className="max-w-[1100px] mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">Support & Contact</h2>
          <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Need help or have a question about RealixData? We're here to help. Submit your message below and our team will get back to you as soon as possible.
          </p>
        </div>

        {/* Quick Links */}
        <div className="flex flex-wrap justify-center gap-3 mb-10">
          {QUICK_LINKS.map((link) => (
            <button
              key={link.label}
              onClick={() => handleQuickLink(link)}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/60 border border-border text-sm text-muted-foreground hover:text-foreground hover:border-emerald-500/40 transition-colors"
              data-testid={`quick-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              <link.icon className="w-3.5 h-3.5" />
              {link.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Form */}
          <Card className="lg:col-span-3 bg-card border-border" id="support-form">
            <CardContent className="p-5 sm:p-7">
              {submitted ? (
                <div className="text-center py-12" data-testid="support-success-message">
                  <CheckCircle className="w-14 h-14 text-emerald-500 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-foreground mb-2">Thanks for reaching out!</h3>
                  <p className="text-muted-foreground mb-6">Your message has been received and we'll respond as soon as possible.</p>
                  <Button variant="outline" onClick={reset} data-testid="support-send-another">Send Another Message</Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="support-name">Name <span className="text-red-400">*</span></Label>
                      <Input id="support-name" placeholder="Your name" value={form.name} onChange={(e) => handleChange("name", e.target.value)} data-testid="support-name-input" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="support-email">Email Address <span className="text-red-400">*</span></Label>
                      <Input id="support-email" type="email" placeholder="you@example.com" value={form.email} onChange={(e) => handleChange("email", e.target.value)} data-testid="support-email-input" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Subject / Topic <span className="text-red-400">*</span></Label>
                    <Select value={form.subject} onValueChange={(v) => handleChange("subject", v)}>
                      <SelectTrigger data-testid="support-subject-select">
                        <SelectValue placeholder="Select a topic" />
                      </SelectTrigger>
                      <SelectContent>
                        {SUBJECT_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="support-message">Message <span className="text-red-400">*</span></Label>
                    <Textarea id="support-message" placeholder="Describe your question or issue..." rows={4} value={form.message} onChange={(e) => handleChange("message", e.target.value)} data-testid="support-message-input" className="resize-none" />
                  </div>

                  {!showOptional && (
                    <button type="button" onClick={() => setShowOptional(true)} className="text-xs text-emerald-500 hover:underline" data-testid="support-show-optional">
                      + Add optional details (account email, property address)
                    </button>
                  )}
                  {showOptional && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
                      <div className="space-y-2">
                        <Label htmlFor="support-account-email">Account Email (if different)</Label>
                        <Input id="support-account-email" type="email" placeholder="account@example.com" value={form.account_email} onChange={(e) => handleChange("account_email", e.target.value)} data-testid="support-account-email-input" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="support-property">Property Address or Listing</Label>
                        <Input id="support-property" placeholder="123 Main St, Miami FL" value={form.property_address} onChange={(e) => handleChange("property_address", e.target.value)} data-testid="support-property-input" />
                      </div>
                    </div>
                  )}

                  <Button type="submit" disabled={submitting} className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white px-8 h-11" data-testid="support-submit-btn">
                    {submitting ? (
                      <span className="flex items-center gap-2"><Send className="w-4 h-4 animate-pulse" /> Sending...</span>
                    ) : (
                      <span className="flex items-center gap-2"><Send className="w-4 h-4" /> Send Message</span>
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>

          {/* Sidebar */}
          <div className="lg:col-span-2 space-y-5">
            {/* Contact Channels */}
            <Card className="bg-card border-border">
              <CardContent className="p-5 sm:p-6">
                <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Mail className="w-4 h-4 text-emerald-500" /> Contact Channels
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-3 p-2.5 rounded-lg bg-secondary/40">
                    <MessageSquare className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-foreground font-medium text-xs">Support</p>
                      <a href="mailto:support@realixdata.com" className="text-emerald-500 hover:underline text-xs">support@realixdata.com</a>
                      <p className="text-muted-foreground text-xs mt-0.5">Bugs, account help, data issues</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-2.5 rounded-lg bg-secondary/40 cursor-pointer hover:bg-secondary/60 transition-colors" onClick={() => navigate("/business")} data-testid="support-business-link">
                    <Briefcase className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-foreground font-medium text-xs">Business / Partnerships</p>
                      <a href="mailto:business@realixdata.com" className="text-emerald-500 hover:underline text-xs" onClick={(e) => e.stopPropagation()}>business@realixdata.com</a>
                      <p className="text-muted-foreground text-xs mt-0.5">Agents, lenders, data providers</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-2.5 rounded-lg bg-secondary/40">
                    <Mail className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-foreground font-medium text-xs">General Contact</p>
                      <a href="mailto:contact@realixdata.com" className="text-emerald-500 hover:underline text-xs">contact@realixdata.com</a>
                      <p className="text-muted-foreground text-xs mt-0.5">Misc questions</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card border-border">
              <CardContent className="p-5 sm:p-6">
                <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-emerald-500" /> Response Time
                </h3>
                <p className="text-sm text-muted-foreground">
                  Most inquiries are answered within <span className="text-foreground font-medium">24-48 hours</span>.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
}
